/**
 * A origem do caso aberto pela operação (E177.F1, GUS-253 A1, ADR-0131).
 *
 * - **Abertura** (`CartaoDaOrigem`, o primeiro cartão da tela 1, só na plataforma e só no processo
 *   novo): sem campo de banco (decisão do revisor de 27/10) — o banco vem da leitura da CCB, pelo
 *   CNPJ do credor, e o cartão o diz. O escritório requerente é opcional, entre os escritórios
 *   clientes; ele só aparece como requerente no exame e o caso fica na operação.
 * - **Mesa** (`EscolhaDaOrigem`, em `InformarOBanco`): quando a leitura não identifica o banco, a
 *   equipe o escolhe entre os cadastrados e, se quiser, o escritório entre os vinculados a ele;
 *   trocar o banco limpa o escritório.
 *
 * O servidor confere de novo — as listas daqui são conveniência.
 */
import { useEffect, useState, type Ref } from 'react';
import type { AxiosInstance } from 'axios';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  escritoriosClientes,
  escritoriosDoBanco,
  listarOrigens,
  type BancoDaOrigemData,
  type OrigemEscolhida,
} from '@/lib/origem_do_caso';
import type { ProcessoData } from '@/lib/processos';

import { CartaoDaAbertura } from './PecasDaAbertura';

const t = ptBR.abertura.origem;

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'pronto'; bancos: BancoDaOrigemData[] };

function useBancosDaOrigem(client: AxiosInstance): Estado {
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });
  useEffect(() => {
    let vivo = true;
    listarOrigens(client)
      .then((bancos) => {
        if (vivo) setEstado({ tipo: 'pronto', bancos });
      })
      .catch(() => {
        if (vivo) setEstado({ tipo: 'erro' });
      });
    return () => {
      vivo = false;
    };
  }, [client]);
  return estado;
}

/** O carregamento e o erro da lista, ou `null` quando ela está pronta. */
function AvisoDaLista({ estado }: { estado: Estado }): JSX.Element | null {
  if (estado.tipo === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  }
  if (estado.tipo === 'erro') {
    return (
      <p role="alert" className="text-body-sm text-danger" data-testid="origem-erro">
        {t.erro}
      </p>
    );
  }
  return null;
}

export interface EscolhaDaOrigemProps {
  client: AxiosInstance;
  valor: OrigemEscolhida;
  onChange: (origem: OrigemEscolhida) => void;
  invalido: boolean;
  disabled?: boolean;
  /** E184.F4 (GUS-145 item 7): o campo do banco, que só existe com a lista pronta — o atalho da mesa o foca. */
  campoDoBanco?: Ref<HTMLSelectElement>;
}

/** A mesa: o banco (obrigatório) e o escritório vinculado a ele (opcional). */
export function EscolhaDaOrigem({
  client,
  valor,
  onChange,
  invalido,
  disabled = false,
  campoDoBanco,
}: EscolhaDaOrigemProps): JSX.Element {
  const estado = useBancosDaOrigem(client);
  if (estado.tipo !== 'pronto') return <AvisoDaLista estado={estado} />;
  if (estado.bancos.length === 0) {
    return (
      <p className="text-body-sm text-fg" data-testid="origem-nenhum-banco">
        {t.nenhumBanco}
      </p>
    );
  }
  const escritorios = escritoriosDoBanco(estado.bancos, valor.banco);
  return (
    <div className="grid gap-sc-4 md:grid-cols-2">
      <Field
        label={t.banco}
        description={t.ajudaDoBanco}
        error={
          invalido ? <span data-testid="origem-banco-obrigatorio">{t.obrigatorio}</span> : undefined
        }
        required
      >
        <Select
          ref={campoDoBanco}
          data-testid="origem-banco"
          value={valor.banco ?? ''}
          disabled={disabled}
          onChange={(evt) =>
            onChange({ banco: evt.target.value === '' ? null : evt.target.value, escritorio: null })
          }
        >
          <option value="">{t.escolhaOBanco}</option>
          {estado.bancos.map((b) => (
            <option key={b.id} value={b.id}>
              {b.nome}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label={t.escritorio}
        description={
          valor.banco !== null && escritorios.length === 0 ? t.nenhumVinculado : t.ajudaDoEscritorio
        }
      >
        <Select
          data-testid="origem-escritorio"
          value={valor.escritorio ?? ''}
          disabled={disabled || escritorios.length === 0}
          onChange={(evt) =>
            onChange({
              banco: valor.banco,
              escritorio: evt.target.value === '' ? null : evt.target.value,
            })
          }
        >
          <option value="">{t.semEscritorio}</option>
          {escritorios.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nome}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

export interface OrigemDaOperacao {
  /** A abertura pela operação num processo novo: só ela informa a origem. */
  ativa: boolean;
  escritorio: string | null;
  escolher: (escritorio: string | null) => void;
  /** O que vai ao payload: o escritório requerente só quando ativa. */
  doPasso: string | null | undefined;
}

/** No processo existente, a origem é a do processo; fora da plataforma, não há origem. */
export function useOrigemDaOperacao(
  daOperacao: boolean | undefined,
  processo: ProcessoData | undefined,
): OrigemDaOperacao {
  const [escritorio, escolher] = useState<string | null>(null);
  const ativa = daOperacao === true && processo === undefined;
  return { ativa, escritorio, escolher, doPasso: ativa ? escritorio : undefined };
}

function EscritorioRequerente({
  client,
  origem,
  disabled,
}: {
  client: AxiosInstance;
  origem: OrigemDaOperacao;
  disabled: boolean;
}): JSX.Element {
  const estado = useBancosDaOrigem(client);
  if (estado.tipo !== 'pronto') return <AvisoDaLista estado={estado} />;
  return (
    <Field label={t.escritorio} description={t.ajudaDoEscritorio}>
      <Select
        data-testid="origem-escritorio"
        value={origem.escritorio ?? ''}
        disabled={disabled}
        onChange={(evt) => origem.escolher(evt.target.value === '' ? null : evt.target.value)}
      >
        <option value="">{t.semEscritorio}</option>
        {escritoriosClientes(estado.bancos).map((e) => (
          <option key={e.id} value={e.id}>
            {`${e.nome} · ${e.bancos.join(', ')}`}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** O cartão "Origem do caso", o primeiro da tela; nada fora da abertura pela operação. */
export function CartaoDaOrigem({
  client,
  origem,
  disabled,
}: {
  client: AxiosInstance;
  origem: OrigemDaOperacao;
  disabled: boolean;
}): JSX.Element | null {
  if (!origem.ativa) return null;
  return (
    <CartaoDaAbertura titulo={t.titulo} testId="abertura-origem">
      <div className="grid gap-sc-4 md:grid-cols-2">
        <div>
          <p className="text-controle text-fg-muted">{t.banco}</p>
          <p className="mt-sc-2 text-controle text-fg" data-testid="origem-banco-pela-leitura">
            {t.bancoPelaLeitura}
          </p>
        </div>
        <EscritorioRequerente client={client} origem={origem} disabled={disabled} />
      </div>
    </CartaoDaAbertura>
  );
}
